import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import { ProgressBar } from 'primereact/progressbar';
import * as actionTypes from '../../redux/actionTypes';
import TeamSelector from './components/TeamSelector';
import FileUploader from './components/FileUploader';
import ProcessingProgress from './components/ProcessingProgress';
import BatchHistory from './components/BatchHistory';
import BatchResultsDialog from './components/BatchResultsDialog';
import { validateBatchUpload } from './utils/fileValidation';
import { downloadBatchResults } from '../../service/dataCategorizationService';
import useTeamAccess from '../../../../common/hooks/useTeamAccess';
import './BatchProcessing.scss';

/**
 * BatchProcessing Component
 *
 * Main container for batch file upload and processing
 *
 * @component
 */
const BatchProcessing = () => {
  const dispatch = useDispatch();
  const toast = useRef(null);

  // Redux state
  const {
    currentBatch,
    batchStatus,
    batches,
    batchHistoryLoading,
    batchResults,
    batchResultsPagination,
    batchLoading,
    uploadProgress,
    error,
    successMessage,
    selectedTeam: reduxSelectedTeam
  } = useSelector(state => state.dataCategorization);

  // Local state
  const selectedTeam = reduxSelectedTeam || '';
  const setSelectedTeam = (team) => {
    dispatch({ type: actionTypes.SET_SELECTED_TEAM, payload: team });
  };
  const [saveResults, setSaveResults] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  // Team-level access for the selected team
  const { hasAccess: hasTeamAccess, isManaged: isTeamManaged, createdBy: teamCreatedBy } = useTeamAccess(selectedTeam);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [viewingBatch, setViewingBatch] = useState(null);
  const [downloadingBatchIds, setDownloadingBatchIds] = useState([]);
  const [reprocessingBatchIds, setReprocessingBatchIds] = useState([]);

  // --- Auto-refresh: poll every 10s for up to 2 min while any batch is PROCESSING ---
  const autoRefreshInterval = useRef(null);
  const autoRefreshTimer = useRef(null);

  const filteredBatches = selectedTeam
    ? (batches || []).filter(b => b.team?.toLowerCase() === selectedTeam.toLowerCase())
    : [];

  const hasProcessingBatch = filteredBatches.some(
    b => b.status === 'PROCESSING'
  );

  useEffect(() => {
    if (hasProcessingBatch && !autoRefreshInterval.current) {
      // Start polling every 10 seconds
      autoRefreshInterval.current = setInterval(() => {
        dispatch({ type: actionTypes.GET_RECENT_BATCHES, payload: { limit: 10 } });
      }, 10000);

      // Auto-stop after 2 minutes
      autoRefreshTimer.current = setTimeout(() => {
        if (autoRefreshInterval.current) {
          clearInterval(autoRefreshInterval.current);
          autoRefreshInterval.current = null;
        }
        autoRefreshTimer.current = null;
      }, 120000);
    } else if (!hasProcessingBatch && autoRefreshInterval.current) {
      // No records in processing — stop immediately
      clearInterval(autoRefreshInterval.current);
      autoRefreshInterval.current = null;
      if (autoRefreshTimer.current) {
        clearTimeout(autoRefreshTimer.current);
        autoRefreshTimer.current = null;
      }
    }

    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
        autoRefreshInterval.current = null;
      }
      if (autoRefreshTimer.current) {
        clearTimeout(autoRefreshTimer.current);
        autoRefreshTimer.current = null;
      }
    };
  }, [hasProcessingBatch, dispatch]);
  // --- End auto-refresh ---

  // Load recent batches on mount
  useEffect(() => {
    dispatch({
      type: actionTypes.GET_RECENT_BATCHES,
      payload: { limit: 10 }
    });

    // Set up progress dispatch callback
    window.dispatchProgress = (percentage) => {
      dispatch({
        type: actionTypes.UPLOAD_BATCH_FILE_PROGRESS,
        payload: percentage
      });
    };
    
    return () => {
      window.dispatchProgress = null;
    };
  }, [dispatch]);

  // Poll batch status when processing
  useEffect(() => {
    if (currentBatch && batchStatus?.status === 'PROCESSING') {
      // Start polling
      const interval = setInterval(() => {
        dispatch({
          type: actionTypes.GET_BATCH_STATUS,
          payload: { batchId: currentBatch.batchId }
        });
      }, 2000); // Poll every 2 seconds

      setPollingInterval(interval);

      // Cleanup
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    } else if (pollingInterval) {
      // Stop polling if not processing
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [currentBatch, batchStatus, dispatch, pollingInterval]);

  // Handle error notifications
  useEffect(() => {
    if (error) {
      console.log('[BatchProcessing] Error occurred:', error);
      if (window._uploadTimeoutId) {
        clearTimeout(window._uploadTimeoutId);
        window._uploadTimeoutId = null;
      }
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error,
        life: 5000,
      });
      dispatch({ type: actionTypes.CLEAR_DATA_CATEGORY_ERROR });
      setIsUploading(false);
    }
  }, [error, dispatch]);

  // Handle success notifications
  useEffect(() => {
    if (successMessage) {
      console.log('[BatchProcessing] Success:', successMessage);
      if (window._uploadTimeoutId) {
        clearTimeout(window._uploadTimeoutId);
        window._uploadTimeoutId = null;
      }
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: successMessage,
        life: 3000,
      });
      setTimeout(() => {
        dispatch({ type: actionTypes.CLEAR_SUCCESS_MESSAGE });
      }, 3000);
      setIsUploading(false);
    }
  }, [successMessage, dispatch]);

  // Handle batch completion
  useEffect(() => {
    if (batchStatus?.status === 'COMPLETED') {
      toast.current?.show({
        severity: 'success',
        summary: 'Processing Complete',
        detail: `Batch processed successfully. ${batchStatus.processedRecords} records categorized.`,
        life: 5000,
      });
      setIsUploading(false);
      // Refresh batch history
      dispatch({
        type: actionTypes.GET_RECENT_BATCHES,
        payload: { limit: 10 }
      });
    } else if (batchStatus?.status === 'FAILED') {
      toast.current?.show({
        severity: 'error',
        summary: 'Processing Failed',
        detail: batchStatus.message || 'Batch processing failed. Please try again.',
        life: 5000,
      });
      setIsUploading(false);
    } else if (batchStatus?.status === 'CANCELLED') {
      toast.current?.show({
        severity: 'warn',
        summary: 'Processing Cancelled',
        detail: 'Batch processing was cancelled.',
        life: 3000,
      });
      setIsUploading(false);
    }
  }, [batchStatus, dispatch]);

  /**
   * Handle file selection
   */
  const handleFileSelect = (file) => {
    console.log('[BatchProcessing] File selected:', file);
  };

  /**
   * Handle file upload
   */
  const handleUpload = (file, selectedRuleIds = []) => {
    console.log('[BatchProcessing] Upload initiated:', { file, selectedTeam, saveResults, selectedRuleIds });

    // Validate
    const validation = validateBatchUpload(file, selectedTeam);
    console.log('[BatchProcessing] Validation result:', validation);

    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join('\n');
      console.error('[BatchProcessing] Validation failed:', errorMessages);
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: errorMessages,
        life: 5000,
      });
      return;
    }

    setIsUploading(true);
    console.log('[BatchProcessing] Dispatching upload action with:', { 
      fileName: file.name, 
      fileSize: file.size,
      team: selectedTeam, 
      saveResults 
    });

    // Set a timeout to reset loading state if no response after 5 minutes
    const timeoutId = setTimeout(() => {
      console.error('[BatchProcessing] Upload timeout - no response after 5 minutes');
      setIsUploading(false);
      toast.current?.show({
        severity: 'error',
        summary: 'Upload Timeout',
        detail: 'The server is taking too long to respond. Please check if the backend is running.',
        life: 8000,
      });
    }, 300000); // 5 minutes

    // Store timeout ID to clear it on success/error
    window._uploadTimeoutId = timeoutId;

    // Dispatch upload action with file, team, and saveResults
    console.log('[BatchProcessing] Dispatching action - action type:', actionTypes.UPLOAD_BATCH_FILE);
    console.log('[BatchProcessing] File object:', file);
    console.log('[BatchProcessing] File is File instance:', file instanceof File);
    
    dispatch({
      type: actionTypes.UPLOAD_BATCH_FILE,
      payload: { 
        file, 
        team: selectedTeam, 
        saveResults,
        selectedRuleIds
      }
    });
  };

  /**
   * Handle cancel processing
   */
  const handleCancelProcessing = (batchId) => {
    dispatch({
      type: actionTypes.CANCEL_BATCH,
      payload: { batchId }
    });
  };

  /**
   * Handle re-process batch
   * @param {string} batchId - Batch ID
   * @param {string} mode - 'ALL' or 'FAILED_ONLY'
   */
  const handleReprocess = (batchId, mode) => {
    const modeLabel = mode === 'ALL' ? 'all rows' : 'failed rows only';
    console.log(`[BatchProcessing] Re-processing batch ${batchId} - ${modeLabel}`);

    setReprocessingBatchIds(prev => [...prev, batchId]);

    toast.current?.show({
      severity: 'info',
      summary: 'Re-processing',
      detail: `Re-processing ${modeLabel} for batch ${batchId}...`,
      life: 3000,
    });

    dispatch({
      type: actionTypes.REPROCESS_BATCH,
      payload: { batchId, mode }
    });
  };

  // Handle reprocess completion
  useEffect(() => {
    if (successMessage && successMessage.includes('re-processed')) {
      setReprocessingBatchIds([]);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error && reprocessingBatchIds.length > 0) {
      setReprocessingBatchIds([]);
    }
  }, [error, reprocessingBatchIds.length]);

  /**
   * Handle view results
   */
  const handleViewResults = (batchId) => {
    const batch = batches?.find(b => b.batchId === batchId) || { batchId };
    setViewingBatch(batch);
    dispatch({
      type: actionTypes.GET_BATCH_RESULTS,
      payload: { batchId, page: 0, size: 50 }
    });
  };

  const handleResultsPageChange = (e) => {
    if (!viewingBatch) return;

    // Extract status filter separately — backend expects it as a dedicated query param
    const allFilters = e.filters || [];
    const statusFilter = allFilters.find(f => f.field === 'status');
    const nonStatusFilters = allFilters.filter(f => f.field !== 'status');

    dispatch({
      type: actionTypes.GET_BATCH_RESULTS,
      payload: {
        batchId: viewingBatch.batchId,
        page: e.page,
        size: e.rows,
        status: statusFilter?.value || '',
        sortField: e.sortField || '',
        sortOrder: e.sortOrder || 1,
        filters: nonStatusFilters
      }
    });
  };

  /**
   * Handle download results — direct service call, generates CSV in browser
   */
  const handleDownload = async (batchId) => {
    if (downloadingBatchIds.includes(batchId)) return;
    setDownloadingBatchIds(prev => [...prev, batchId]);
    try {
      const response = await downloadBatchResults(batchId);
      const data = response.data?.content || response.data || [];
      const rows = Array.isArray(data) ? data : [];

      if (rows.length === 0) {
        toast.current?.show({ severity: 'warn', summary: 'No Data', detail: 'No results found for this batch.', life: 3000 });
        return;
      }

      // Build CSV — dynamically derive columns from actual data shape
      const escape = (val) => {
        if (val == null) return '""';
        // If a value is a JSON string (e.g. inputData stored as serialized object), parse and re-flatten
        let str = typeof val === 'object' ? JSON.stringify(val) : String(val);
        return `"${str.replace(/"/g, '""')}"`;
      };

      // Flatten each row: if a field value is a JSON-serialized object, expand its keys inline
      const flattenRow = (row) => {
        const flat = {};
        Object.entries(row).forEach(([key, value]) => {
          if (typeof value === 'string') {
            try {
              const parsed = JSON.parse(value);
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                // Merge the parsed object's keys directly into the flat row
                Object.entries(parsed).forEach(([k, v]) => { flat[k] = v; });
                return;
              }
            } catch (_) { /* not JSON — use as-is */ }
          }
          flat[key] = value;
        });
        return flat;
      };

      const flatRows = rows.map(flattenRow);

      // Collect all unique headers across every row (preserves insertion order of first row)
      const allKeys = [...new Set(flatRows.flatMap(r => Object.keys(r)))];

      const csvRows = [
        allKeys.join(','),
        ...flatRows.map(r => allKeys.map(k => escape(r[k])).join(','))
      ];
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `batch-results-${batchId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.current?.show({ severity: 'success', summary: 'Downloaded', detail: `${rows.length} records exported.`, life: 3000 });
    } catch (err) {
      console.error('[BatchProcessing] Download failed:', err);
      toast.current?.show({ severity: 'error', summary: 'Download Failed', detail: err.message || 'Could not download results.', life: 5000 });
    } finally {
      setDownloadingBatchIds(prev => prev.filter(id => id !== batchId));
    }
  };

  const isProcessing = batchStatus?.status === 'PROCESSING';
  const showProgress = currentBatch && (isProcessing || batchStatus?.status === 'COMPLETED' ||
                                         batchStatus?.status === 'FAILED' || batchStatus?.status === 'CANCELLED');

  return (
    <div className="batch-processing-container">
      <Toast ref={toast} />

      <div className="header">
        <div>
          <h2>Batch Processing</h2>
          <p className="subtitle">Upload files for bulk categorization</p>
        </div>
      </div>

      <div className="upload-section">
        <TeamSelector
          selectedTeam={selectedTeam}
          onTeamChange={setSelectedTeam}
          saveResults={saveResults}
          onSaveResultsChange={setSaveResults}
          disabled={isUploading || isProcessing}
        />

        <Divider />

        {/* Access restriction notice */}
        {selectedTeam && isTeamManaged && !hasTeamAccess && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: '0.5rem', fontSize: '0.88rem', color: '#b91c1c' }}>
            <i className="pi pi-lock" style={{ fontSize: '1rem' }} />
            <span>
              You don’t have access to process batches for <strong>{selectedTeam}</strong>.
              {teamCreatedBy && <> Contact the team admin: <strong>{teamCreatedBy}</strong>.</>}
            </span>
          </div>
        )}

        <FileUploader
          onFileSelect={handleFileSelect}
          onUpload={handleUpload}
          disabled={!selectedTeam || isProcessing || (isTeamManaged && !hasTeamAccess)}
          loading={isUploading}
          selectedTeam={selectedTeam}
        />

        {isUploading && uploadProgress > 0 && (
          <div className="upload-progress-section" style={{ marginTop: '1rem' }}>
            <h4>Uploading File...</h4>
            <ProgressBar 
              value={uploadProgress} 
              displayValueTemplate={(value) => `${value}%`}
              style={{ height: '24px' }}
            />
          </div>
        )}
      </div>

      {showProgress && (
        <>
          <Divider />
          <ProcessingProgress
            batchStatus={batchStatus}
            onCancel={handleCancelProcessing}
            onViewResults={handleViewResults}
          />
        </>
      )}

      <Divider />

      <BatchHistory
        batches={filteredBatches}
        loading={batchHistoryLoading}
        onViewResults={handleViewResults}
        onDownload={handleDownload}
        onReprocess={handleReprocess}
        downloadingBatchIds={downloadingBatchIds}
        reprocessingBatchIds={reprocessingBatchIds}
        selectedTeam={selectedTeam}
        onRefresh={() => dispatch({ type: actionTypes.GET_RECENT_BATCHES, payload: { limit: 10 } })}
      />

      <BatchResultsDialog
        visible={!!viewingBatch}
        onHide={() => setViewingBatch(null)}
        batch={viewingBatch}
        results={batchResults || []}
        loading={batchLoading}
        pagination={batchResultsPagination}
        onPageChange={handleResultsPageChange}
      />
    </div>
  );
};

export default BatchProcessing;

